import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@material-ui/core';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';

import { DeviceContext } from '@vyce/core/src/contexts';
import { GRID_PAGE_SIZE, MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { AppA, AppDataGrid, GridCellWithAvatar } from '@vyce/core/src/components';
import { getRespondentsByAnswerRequest } from '@vyce/core/src/api/forms';
import { FormRespondent, User } from '@vyce/core/src/types';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';

const SORTABLE_FIELDS = ['name', 'email', 'phone'] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  companyId: string;
  formId: string;
  questionId: string;
  answerValue: string;
  answerLabel: string;
  user: User;
}

export const FormAnswerRespondentsDialog: React.FC<Props> = ({
  open,
  onClose,
  companyId,
  formId,
  questionId,
  answerValue,
  answerLabel,
  user,
}) => {
  const { isMobile } = useContext(DeviceContext);
  const [loading, setLoading] = useState(false);
  const [respondents, setRespondents] = useState<FormRespondent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounceValue(searchInput, 300);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const { createPrivateChat } = useChatRequests();

  const chatCreator = useMemo(
    () => ({
      uid: user.uuid,
      name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      phone: user.phone,
      avatarUrl: user.photo?.url,
    }),
    [user.uuid, user.first_name, user.last_name, user.email, user.phone, user.photo?.url]
  );

  const goToChat = useCallback(
    async (respondent: FormRespondent) => {
      await createPrivateChat({
        creator: chatCreator,
        chatUser: {
          uid: respondent.uid || '',
          name: respondent.name,
          email: respondent.email,
          phone: respondent.phone,
          avatarUrl: respondent.avatarUrl,
        },
        companyId,
      });
    },
    [createPrivateChat, companyId, chatCreator]
  );

  const loadRespondents = useCallback(async () => {
    if (!open || !companyId || !formId || !user.uuid || !questionId) return;
    try {
      setLoading(true);
      const rawField = sortModel[0]?.field;
      const sortField: (typeof SORTABLE_FIELDS)[number] = SORTABLE_FIELDS.includes(
        rawField as (typeof SORTABLE_FIELDS)[number]
      )
        ? (rawField as (typeof SORTABLE_FIELDS)[number])
        : 'name';
      const sortDirection = sortModel[0]?.sort === 'desc' ? 'desc' : 'asc';
      const searchForApi = debouncedSearch?.trim() || null;

      const response = await getRespondentsByAnswerRequest({
        companyId,
        formId,
        userId: user.uuid,
        questionId,
        answerValue,
        search: searchForApi,
        limit: GRID_PAGE_SIZE,
        offset: page * GRID_PAGE_SIZE,
        sortField,
        sortDirection,
      });

      setRespondents(response.respondents);
      setTotal(typeof response.total === 'number' ? response.total : 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [open, companyId, formId, user.uuid, questionId, answerValue, page, debouncedSearch, sortModel]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        minWidth: 120,
        disableColumnMenu: true,
        renderCell: params => (
          <GridCellWithAvatar
            avatarUrl={params.row.avatarUrl}
            avatarPlaceholder={getAvatar()}
            name={params.row.name}
            createPrivateChat={params.row?.uid ? () => goToChat(params.row as FormRespondent) : undefined}
          />
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.5,
        minWidth: 180,
        disableColumnMenu: true,
        renderCell: params => (
          <AppA cy-test-id="user-email" content={params.row.email} href={`mailto: ${params.row.email}`} />
        ),
      },
      {
        field: 'phone',
        headerName: 'Phone Number',
        disableColumnMenu: true,
        minWidth: 180,
        renderCell: params => <AppA content={params.row.phone} href={`tel: ${params.row.phone}`} />,
      },
    ],
    [goToChat]
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const next: GridSortModel = model.length ? model : [{ field: 'name', sort: 'asc' }];
      const prev = sortModel[0];
      const n = next[0];
      if ((prev?.field ?? 'name') === (n?.field ?? 'name') && (prev?.sort ?? 'asc') === (n?.sort ?? 'asc')) {
        return;
      }
      setSortModel(next);
      setPage(0);
    },
    [sortModel]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(p => (p === newPage ? p : newPage));
  }, []);

  const handleClose = () => {
    onClose();
    setRespondents([]);
    setTotal(0);
    setPage(0);
    setSearchInput('');
    setSortModel([{ field: 'name', sort: 'asc' }]);
  };

  const sortFieldDep = sortModel[0]?.field ?? 'name';
  const sortOrderDep = sortModel[0]?.sort ?? 'asc';
  const debouncedSearchDep = debouncedSearch ?? '';

  useEffect(() => {
    if (!open) return;
    void loadRespondents();
  }, [
    open,
    companyId,
    formId,
    user.uuid,
    questionId,
    answerValue,
    page,
    debouncedSearchDep,
    sortFieldDep,
    sortOrderDep,
  ]);

  useEffect(() => {
    if (!open) return;
    setPage(p => (p !== 0 ? 0 : p));
  }, [debouncedSearchDep, open]);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      maxWidth="lg"
      fullScreen={isMobile}
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>{`Who answered: ${answerLabel}`}</DialogTitle>

      <DialogContent style={{ minWidth: isMobile ? '100%' : 800 }}>
        <Box marginBottom={2}>
          <AppSearchInput
            placeholder="Search"
            onChange={e => setSearchInput(e.target.value)}
            value={searchInput}
            isSmall
            smallRounding
            isBorder
            expanded={true}
          />
        </Box>
        <AppDataGrid
          noPaper={true}
          rows={respondents}
          getRowId={row => row.id}
          columns={columns}
          height="calc(50vh)"
          rowCount={total}
          loading={loading}
          paginationMode="server"
          page={page}
          pageSize={GRID_PAGE_SIZE}
          rowsPerPageOptions={[GRID_PAGE_SIZE]}
          onPageChange={handlePageChange}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          disableSelectionOnClick
        />
      </DialogContent>

      <DialogActions>
        <Button size="large" variant="outlined" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
